"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { JobPosting, CareersPageConfig } from "@/types/careers";
import { getJobs, getCareersPageConfig } from "@/lib/actions/careers.actions";
import JobCard from "./JobCard";
import JobCardSkeleton from "./JobCardSkeleton";
import { gsap } from "@/lib/gsap";

interface CareersContentProps {
  showHeader?: boolean;
}

export default function CareersContent({ showHeader = true }: CareersContentProps) {
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [pageConfig, setPageConfig] = useState<CareersPageConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");

  // Get unique departments from jobs
  const departments = useMemo(() => {
    const depts = new Set(jobs.map((job) => job.metadata.department));
    return ["All", ...Array.from(depts).sort()];
  }, [jobs]);

  // Filter jobs by selected department
  const filteredJobs = useMemo(() => {
    if (selectedDepartment === "All") return jobs;
    return jobs.filter((job) => job.metadata.department === selectedDepartment);
  }, [jobs, selectedDepartment]);

  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [jobsData, config] = await Promise.all([
          getJobs(),
          getCareersPageConfig(),
        ]);
        setJobs(jobsData);
        setPageConfig(config);
      } catch (error) {
        console.error("Error fetching careers data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // GSAP Animations
  useEffect(() => {
    if (isLoading || !jobs.length) return;

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 90%",
          end: "top 45%",
          toggleActions: "play none none reverse",
        },
      });

      if (showHeader && headerRef.current && headingRef.current) {
        timeline
          .fromTo(
            headerRef.current,
            { opacity: 0, scale: 0.95 },
            { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out" }
          )
          .fromTo(
            headingRef.current,
            { opacity: 0, y: 40, filter: "blur(8px)" },
            {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              duration: 1,
              ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            },
            "-=0.1"
          );
      }

      const jobCards = cardsRef.current?.children;
      if (jobCards && jobCards.length > 0) {
        gsap.fromTo(
          jobCards,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            stagger: 0.1,
            scrollTrigger: {
              trigger: cardsRef.current,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading, filteredJobs, showHeader]);

  return (
    <section ref={sectionRef} className="container pt-8 md:pt-12 pb-16 md:pb-24">
      {/* Header */}
      {showHeader && pageConfig && (
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div
            className="flex items-center justify-center mb-4"
            ref={headerRef}
          >
            <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
            <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
              Careers
            </p>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-theme-secondary"
            ref={headingRef}
          >
            {pageConfig.hero.title}
          </h2>
          {pageConfig.hero.description && (
            <p className="text-lg text-gray-600 mt-4">
              {pageConfig.hero.description}
            </p>
          )}
        </div>
      )}

      {/* Department Filter */}
      {!isLoading && jobs.length > 0 && (
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {departments.map((dept) => (
            <button
              key={dept}
              onClick={() => setSelectedDepartment(dept)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                selectedDepartment === dept
                  ? "bg-[#284D93] text-white shadow-lg shadow-[#284D93]/25"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
              }`}
            >
              {dept}
              {dept !== "All" && (
                <span className={`ml-2 text-xs ${
                  selectedDepartment === dept ? "text-white/70" : "text-gray-400"
                }`}>
                  ({jobs.filter((j) => j.metadata.department === dept).length})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Job Cards Grid */}
      <div
        className="flex flex-wrap justify-center gap-6 md:gap-8 mx-auto"
        ref={cardsRef}
      >
        {isLoading ? (
          <>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-[400px]">
                <JobCardSkeleton count={1} />
              </div>
            ))}
          </>
        ) : filteredJobs.length > 0 ? (
          filteredJobs.map((job) => (
            <div key={job.id} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-[400px]">
              <JobCard job={job} />
            </div>
          ))
        ) : (
          <div className="w-full text-center py-12">
            <p className="text-gray-500 text-lg">
              {selectedDepartment === "All"
                ? "No open positions at the moment. Check back soon!"
                : `No open positions in ${selectedDepartment} at the moment.`}
            </p>
          </div>
        )}
      </div>

      {/* Equal Opportunity Statement */}
      {!isLoading && (
        <div className="max-w-3xl mx-auto text-center mt-16 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Equal Opportunity Employer
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            RevealSite is an equal opportunity employer. We celebrate diversity and are
            committed to creating an inclusive environment for all employees. All qualified
            applicants will receive consideration for employment without regard to race,
            color, religion, gender, gender identity or expression, sexual orientation,
            national origin, genetics, disability, age, or veteran status.
          </p>
        </div>
      )}
    </section>
  );
}
